import _ from 'lodash';
import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';

import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import CloseIcon from '@mui/icons-material/Close';
import ArchiveIcon from '@mui/icons-material/Archive';

import { DarkInfoTooltip } from 'widgets-v6/tooltip';
import { DialogTitle } from 'widgets-v6/modal';
import { HealthChip } from 'widgets-v6/health-chip';
import { SingleSelect } from 'widgets-v6/select';
import InlineTooltip from 'widgets-v5/inline-tooltip';
import { CampaignIndicator } from 'widgets-v6/lifecycle-and-health-indicator';

import flags from 'containers/flags/service';
import StatusToggle from 'widgets-v5/status-toggle';
import { formatNumber_currency, formatDate_long } from 'utils/formatting';
import { isInternalUser } from 'states/profile/business-rules';
import {
    canToggleStatus,
    canArchiveCampaign,
    canEditCampaign,
    canDuplicateCampaign,
    isCampaignLocked,
    isCampaignLockedOrArchived,
    isCampaignLegacy,
    isUsingGuaranteedInventoryType,
} from 'states/resources/campaigns/business-logic';
import { Summary, useCampaignSummary } from '../campaign-summary';
import { CampaignDuplicateModal } from '../campaign-duplicate-modal';
import { archiveCampaign, openArchiveConfirmation, closeArchiveConfirmation } from '../../actions';
import { openCampaignDuplicateModal } from '../campaign-duplicate-modal';
import { HEALTH_STATUS } from 'common/constants/lifecycle-and-health-mappings';

const useStyles = makeStyles(theme => ({
    cardWrapper: {
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        height: 160,
    },
    cardHeader: {
        position: 'relative',
        marginBottom: 5,
    },
    cardTitle: {
        marginTop: 5,
    },
    cardActions: {
        position: 'absolute',
        right: 0,
    },
    notes: {
        width: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    drawer: {
        width: 550,
        padding: theme.spacing(2),
    },
    lifecycleDetails: {
        display: 'flex',
        flexDirection: 'column',
    },
    scrollableContent: {
        overflowY: 'auto',
        flexGrow: 1, // Allow this element to grow and fill remaining space
    },
}));

const healthStatus = {
    bad: {
        label: 'Critical',
        color: '#f36a7f',
    },
    good: {
        label: 'On Track',
        color: '#13baaa',
    },
    ok: {
        label: 'Behind',
        color: '#f8cf61',
    },
    pending: {
        label: 'Scheduled',
        color: '#9fbfff',
    },
};

const CampaignDetail = props => {
    const {
        campaign,
        openCampaignEditForm,
        duplicateCampaign,
        openCampaignHistory,
        toggleCampaignStatus,
        campaignCreator,
        campaignHealth,
    } = props;
    const togglePosition = campaign.paused ? 'left' : 'right';
    const isAgencyMargin = campaign.revenueModel === 'agencyMargin';
    const [isNotesOverflowed, setOverflow] = useState(false);
    const [dialogOpenLegacy, setDialogOpenLegacy] = useState(false);
    const [duplicateStatusLegacy, setDuplicateStatusLegacy] = useState('pending');
    const [duplicateBehaviourLegacy, setDuplicateBehaviourLegacy] = useState(null);
    const isDuplicatingLegacy = useRef(false);
    const notesRef = useRef();
    const classes = useStyles();
    const dispatch = useDispatch();
    const { fetchArchivedAdsAndCreatives, archivedAdsAndCreatives } = useCampaignSummary();

    useEffect(() => {
        fetchArchivedAdsAndCreatives({ campaignId: campaign.id });
    }, [campaign.id]);

    const hasArchivedAdsOrCreativesLegacy =
        (archivedAdsAndCreatives.ads.length || archivedAdsAndCreatives.creatives.length) &&
        (archivedAdsAndCreatives.ads.some(({ isArchived }) => isArchived) ||
            archivedAdsAndCreatives.creatives.some(({ isArchived }) => isArchived));

    useEffect(() => {
        if (!notesRef.current) {
            return;
        }
        let resize = () => {
            setOverflow(notesRef.current.scrollWidth > notesRef.current.clientWidth);
        };
        setOverflow(notesRef.current.scrollWidth > notesRef.current.clientWidth);
        window.addEventListener('resize', resize);
        return () => {
            window.removeEventListener('resize', resize);
        };
    }, [notesRef.current]);

    const handleClickOpen = () => {
        dispatch(openCampaignDuplicateModal(campaign));
    };

    const handleLegacyClickOpen = () => {
        setDialogOpenLegacy(true);
    };

    const handleLegacyClose = () => {
        setDuplicateStatusLegacy('pending');
        setDialogOpenLegacy(false);
        setDuplicateBehaviourLegacy(null);
    };

    const handleClickArchive = () => {
        dispatch(openArchiveConfirmation());
    };

    return (
        <Card className={classes.cardWrapper}>
            <div className={classes.cardHeader}>
                <Grid container>
                    <Grid item xs={5} lg={6}>
                        <Grid container spacing={1} alignItems="center">
                            {canToggleStatus(campaign) && (
                                <Grid item>
                                    <InlineTooltip
                                        isEnabled={
                                            !campaign.paused &&
                                            isUsingGuaranteedInventoryType(campaign)
                                        }
                                        position="right"
                                        tooltip={'Any pauses should be made through the publisher'}
                                        left={-18}
                                    >
                                        <StatusToggle
                                            status={campaign.status}
                                            onClick={toggleCampaignStatus}
                                            togglePosition={togglePosition}
                                            disabled={
                                                !campaign.paused &&
                                                isUsingGuaranteedInventoryType(campaign)
                                            }
                                        />
                                    </InlineTooltip>
                                </Grid>
                            )}
                            <Grid item>
                                <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    className={classes.cardTitle}
                                >
                                    Campaign Details
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={7} lg={6}>
                        <Summary campaignId={campaign.id} />
                        {canEditCampaign(campaign) && (
                            <Tooltip
                                title={
                                    <Typography variant="body2">
                                        {isCampaignLockedOrArchived(campaign)
                                            ? 'Edit is disabled for locked/archived campaign'
                                            : 'Edit'}
                                    </Typography>
                                }
                            >
                                <span>
                                    <IconButton
                                        onClick={openCampaignEditForm}
                                        size="small"
                                        disabled={isCampaignLockedOrArchived(campaign)}
                                    >
                                        <EditOutlinedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        {canDuplicateCampaign() && (
                            <Tooltip
                                title={
                                    <Typography variant="body2">
                                        {isCampaignLocked(campaign)
                                            ? 'Duplication is disabled for locked/archived campaign'
                                            : 'Duplicate'}
                                    </Typography>
                                }
                            >
                                <span>
                                    <IconButton
                                        onClick={
                                            flags.isEnabled('efb_760_validate_campaign_duplication')
                                                ? handleClickOpen
                                                : handleLegacyClickOpen
                                        }
                                        size="small"
                                        disabled={isCampaignLocked(campaign)}
                                    >
                                        <FileCopyOutlinedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        {
                            <Tooltip
                                title={
                                    <Typography variant="body2">
                                        {' '}
                                        {isCampaignLegacy(campaign)
                                            ? 'History is disabled for legacy campaign'
                                            : 'History'}
                                    </Typography>
                                }
                            >
                                <span>
                                    <IconButton
                                        onClick={openCampaignHistory}
                                        size="small"
                                        className={classes.actionButton}
                                        disabled={isCampaignLegacy(campaign)}
                                    >
                                        <AccessTimeOutlinedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        }
                        {canArchiveCampaign(campaign) && (
                            <Tooltip
                                title={
                                    <Typography variant="body2">
                                        {isCampaignLocked(campaign)
                                            ? 'Archive is disabled for locked/archived campaign'
                                            : 'Archive'}
                                    </Typography>
                                }
                            >
                                <span>
                                    <IconButton
                                        onClick={handleClickArchive}
                                        size="small"
                                        disabled={isCampaignLocked(campaign)}
                                    >
                                        <ArchiveIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Grid>
                </Grid>
            </div>
            <div className={classes.scrollableContent}>
                {_.map(campaign.custom_fields, customField => (
                    <div key={customField.name}>
                        {customField.value === '' ? null : (
                            <Typography variant="caption" key={customField.name}>
                                <strong>{customField.name}:</strong> {customField.value}
                            </Typography>
                        )}
                    </div>
                ))}
                {!isAgencyMargin && campaign.campaign_max_total_spend_local === 0 ? null : (
                    <div>
                        <Typography variant="caption">
                            <strong>
                                {isAgencyMargin ? 'Revenue Budget' : 'Total Cost Budget'}:
                            </strong>{' '}
                            {formatNumber_currency(
                                isAgencyMargin
                                    ? campaign.total_billings_local
                                    : campaign.campaign_max_total_spend_local
                            )}
                        </Typography>
                    </div>
                )}
                {campaignCreator ? (
                    <div>
                        <Typography variant="caption">
                            <strong>Assignee:</strong> {campaignCreator}
                        </Typography>
                    </div>
                ) : null}
                {campaign.notes ? (
                    <Tooltip
                        title={<Typography variant="body2">{campaign.notes}</Typography>}
                        disableHoverListener={!isNotesOverflowed}
                    >
                        <div ref={notesRef} className={classes.notes}>
                            <Typography variant="caption">
                                <strong>Notes:</strong> {campaign.notes}
                            </Typography>
                        </div>
                    </Tooltip>
                ) : null}
                {flags.isEnabled('efb_390_new_lifecycle_and_health_statuses_label') ? (
                    campaign.budget_allocation_method === 'auto' &&
                    campaignHealth &&
                    campaignHealth !== HEALTH_STATUS.none ? (
                        <div>
                            <CampaignIndicator
                                status={campaign.lifecycle?.current_state}
                                health={campaignHealth}
                                showHealth
                            />
                        </div>
                    ) : null
                ) : campaign.budget_allocation_method === 'auto' && campaignHealth ? (
                    <div>
                        <Typography variant="caption">
                            <strong>Campaign Status </strong>
                            <DarkInfoTooltip
                                title={
                                    'Green, yellow, or red circles indicate whether the campaign is pacing within 90%, within 80%, or less than 80%.'
                                }
                                placement="bottom-start"
                            />
                            <strong> : </strong>
                        </Typography>
                        <HealthChip
                            label={healthStatus[campaignHealth]?.label || campaignHealth}
                            customcolor={
                                healthStatus[campaignHealth]?.color || healthStatus.pending.color
                            }
                            size="small"
                        />
                    </div>
                ) : null}
                {flags.isEnabled('efb_201_campaign_expiry') && isInternalUser() && (
                    <div className={classes.lifecycleDetails}>
                        <Typography variant="caption">
                            <strong>Campaign Start Date:</strong>{' '}
                            {formatDate_long(campaign.lifecycle.start_date)}
                        </Typography>
                        <Typography variant="caption">
                            <strong>Campaign End Date:</strong>{' '}
                            {formatDate_long(campaign.lifecycle.end_date)}
                        </Typography>
                        <Typography variant="caption">
                            <strong>Current Lifecycle State:</strong>{' '}
                            {campaign.lifecycle.current_state}
                        </Typography>
                        <Typography variant="caption">
                            <strong>Latest Possible Start Date:</strong>{' '}
                            {formatDate_long(campaign.lifecycle.latest_possible_start_date)}
                        </Typography>
                        <Typography variant="caption">
                            <strong>Latest Possible End Date:</strong>{' '}
                            {formatDate_long(campaign.lifecycle.latest_possible_end_date)}
                        </Typography>
                        <Typography variant="caption">
                            <strong>Finalized Date:</strong>{' '}
                            {formatDate_long(campaign.lifecycle.finalized_at)}
                        </Typography>
                        <Typography variant="caption">
                            <strong>Locked Date:</strong>{' '}
                            {formatDate_long(campaign.lifecycle.locked_at)}
                        </Typography>
                        <Typography variant="caption">
                            <strong>Purged Date:</strong>{' '}
                            {formatDate_long(campaign.lifecycle.purged_at)}
                        </Typography>
                        <Typography variant="caption">
                            <strong>Ignore Flight Dates Verification:</strong>{' '}
                            {campaign.lifecycle.ignore_flight_dates ? 'True' : 'False'}
                        </Typography>
                        <Typography variant="caption">
                            <strong>Is Campaign Archived:</strong>{' '}
                            {campaign.isArchived ? 'True' : 'False'}
                        </Typography>
                    </div>
                )}
            </div>
            {flags.isEnabled('efb_760_validate_campaign_duplication') ? (
                <CampaignDuplicateModal campaign={campaign} duplicateCampaign={duplicateCampaign} />
            ) : (
                <Dialog open={dialogOpenLegacy} onClose={handleLegacyClose}>
                    <DialogTitle className={classes.title}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Typography variant="h6">Duplicating Campaign...</Typography>
                            <IconButton
                                onClick={handleLegacyClose}
                                disabled={duplicateStatusLegacy === 'loading'}
                                size="large"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            The new campaign will be paused and will require new start/end dates.
                            Any scheduled reports or shared reports will also be removed.
                        </DialogContentText>
                        {hasArchivedAdsOrCreativesLegacy ? (
                            <DialogContentText>
                                <SingleSelect
                                    required
                                    placeholder="Select a behaviour"
                                    value={duplicateBehaviourLegacy}
                                    onChange={setDuplicateBehaviourLegacy}
                                    label="This campaign contains archived ads or creatives. Include archived items in duplicated campaign?"
                                    options={[
                                        {
                                            label: 'Duplicate and unarchive items',
                                            value: 'duplicateUnarchive',
                                        },
                                        {
                                            label: 'Duplicate, but keeps items archived',
                                            value: 'duplicateKeep',
                                        },
                                        {
                                            label: 'Skip archived items',
                                            value: 'skipArchived',
                                        },
                                    ]}
                                />
                            </DialogContentText>
                        ) : null}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={async () => {
                                if (!isDuplicatingLegacy.current) {
                                    setDuplicateStatusLegacy('loading');
                                    isDuplicatingLegacy.current = true;
                                    await duplicateCampaign({
                                        duplicateBehaviour: duplicateBehaviourLegacy,
                                    });
                                    handleLegacyClose();
                                    setTimeout(() => {
                                        isDuplicatingLegacy.current = false;
                                    }, 1000);
                                }
                            }}
                            color="primary"
                            variant="contained"
                            disabled={
                                duplicateStatusLegacy === 'loading' ||
                                (hasArchivedAdsOrCreativesLegacy && !duplicateBehaviourLegacy)
                            }
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            <ArchiveConfirmation campaign={campaign} />
        </Card>
    );
};

function ArchiveConfirmation(props) {
    const { campaign } = props;

    const [status, setStatus] = useState('pending');
    const dispatch = useDispatch();
    const { isArchiveConfirmationOpen } = useSelector(state => state.campaignsOverview);

    const handleCancel = () => {
        dispatch(closeArchiveConfirmation());
    };

    const handleArchive = async () => {
        try {
            if (status === 'loading') {
                return;
            }
            setStatus('loading');
            await dispatch(archiveCampaign(campaign.id));
            setStatus('pending');
            handleCancel();
        } catch (err) {
            setStatus('pending');
        }
    };

    if (!campaign) {
        return null;
    }

    return (
        <Dialog disableEscapeKeyDown maxWidth="sm" open={isArchiveConfirmationOpen}>
            <DialogTitle>
                #{campaign.id} {campaign.name}
            </DialogTitle>
            <DialogContent>
                This campaign will be permanently archived and can no longer be edited or set live.
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleArchive}
                    disabled={status === 'loading'}
                    variant="outlined"
                    color="primary"
                >
                    Archive
                </Button>
                <Button autoFocus onClick={handleCancel}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default CampaignDetail;
